import useScrollReset from "@hooks/ScrollHook";
import { NftTabs } from "@components/tabs/NftTabs";
import { Link, useNavigate, useParams } from "react-router-dom";
import { errorPromise, useApi } from "@hooks/ApiHook";
import ActionDropdown from "@components/dropdown/ActionDropdown";
import { TraitsTab } from "@app/item/tabs/TraitsTab";
import { useAccount } from "@contexts/AccountContext";
import NftSellItemBox from "@components/nft/NftSellItemBox";
import { useWasmQuery } from "@hooks/WasmQueryHook";
import { CollectionResponse } from "baaanggg";
import { NftBuyItemBox } from "@components/nft/NftBuyItemBox";
import { NftBidItemBox } from "@components/nft/NftBidItemBox";
import { BidsTab } from "@app/item/tabs/BidsTab";
import { DetailTab } from "./tabs/DetailTab";
import {
  getChainRegistryAssetList,
  getChainRegistryChainInfo,
} from "@goblinhunt/cosmes/registry";
import { useEffect } from "react";
import NftTransferItemBox from "@components/nft/NftTransferItemBox";

export function NftPage() {
  useScrollReset();
  const { collectionQuery, tokenId } = useParams();
  const navigate = useNavigate();
  const { address } = useAccount();

  const { data: nft, refresh: refreshNft } = useApi((api) =>
    api.item.nft(collectionQuery!, tokenId!)
  );

  const { data: traits } = useApi(
    (api) => {
      if (!nft) return errorPromise();
      return api.item.nftTraits(nft.id);
    },
    [nft]
  );

  const { data: collection } = useApi(
    (api) => {
      if (!nft) return errorPromise();
      return api.collection.find(nft.collectionId);
    },
    [nft]
  );

  const { data: bidInfo } = useWasmQuery(
    (wasmx) => {
      if (!collection || !nft) return errorPromise();
      return wasmx.collection.query.bidByNft({
        contract: collection.collectionContract,
        data: {
          token_id: nft.tokenId,
        },
      });
    },
    [collection, nft]
  );

  return (
    <main className="mt-24">
      <section className="relative pb-24 pt-12 lg:py-24">
        <picture className="pointer-events-none absolute inset-0 -z-10 dark:hidden">
          <img
            src="/src/img/gradient_light.jpg"
            alt="gradient"
            className="h-full w-full"
          />
        </picture>
        <div className="container">
          <div className="md:flex md:flex-wrap">
            <figure className="mb-8 md:w-2/5 md:flex-shrink-0 md:flex-grow-0 md:basis-auto lg:w-1/2">
              <img
                src={nft?.image}
                alt="item"
                className="w-full cursor-pointer rounded-2.5xl"
              />
            </figure>

            <div className="md:w-3/5 md:basis-auto md:pl-8 lg:w-1/2 lg:pl-[3.75rem]">
              <div className="mb-3 flex">
                <div className="flex items-center">
                  <Link
                    to={`/collection/${collection?.slug}`}
                    className="mr-2 text-lg font-bold text-jacarta-500"
                  >
                    {collection?.name}
                  </Link>
                </div>

                <div className="ml-auto flex space-x-2">
                  <ActionDropdown
                    items={[
                      {
                        text: "Refresh",
                        eventHandler: () => refreshNft(),
                      },
                    ]}
                  />
                </div>
              </div>

              <h1 className="mb-4 font-display text-4xl font-semibold text-jacarta-700 dark:text-white">
                {nft?.name}
              </h1>

              <p className="mb-5 dark:text-gray-400">{nft?.description}</p>

              <div className="mb-2 flex flex-wrap">
                <Link
                  to={`/user/${collection?.owner?.username}`}
                  className="mb-4 mr-8 flex"
                >
                  <figure className="mr-4 shrink-0">
                    <div className="relative block">
                      <img
                        src={
                          collection?.owner?.thumbUrl ??
                          "/src/img/logo/transparant_logo.png"
                        }
                        alt="avatar 7"
                        className="w-12 rounded-2lg"
                        loading="lazy"
                      />
                    </div>
                  </figure>
                  <div className="flex flex-col justify-center">
                    <span className="block text-sm text-jacarta-400 dark:text-white">
                      Creator
                    </span>
                    <div className="block text-jacarta-500">
                      <span className="text-sm font-bold">
                        {collection?.owner?.username}
                      </span>
                    </div>
                  </div>
                </Link>

                <Link
                  to={
                    nft?.ownerAddress && address
                      ? "/profile"
                      : `/user/${nft?.owner?.username}`
                  }
                  className="mb-4 mr-8 flex"
                >
                  <figure className="mr-4 shrink-0">
                    <div className="relative block">
                      <img
                        src={
                          nft?.owner?.thumbUrl ??
                          "/src/img/logo/transparant_logo.png"
                        }
                        alt="avatar 7"
                        className="w-12 rounded-2lg"
                        loading="lazy"
                      />
                    </div>
                  </figure>
                  <div className="flex flex-col justify-center">
                    <span className="block text-sm text-jacarta-400 dark:text-white">
                      Owner
                    </span>
                    <div className="block text-jacarta-500">
                      <span className="text-sm font-bold">
                        {nft?.owner?.username}
                      </span>
                    </div>
                  </div>
                </Link>
              </div>

              {address == nft?.ownerAddress && nft && collection && (
                <NftSellItemBox collection={collection} nft={nft} 
                    onTransferSuccess={refreshNft}
                 />
              )}

              {nft?.ownerAddress &&
                address != nft.ownerAddress &&
                nft &&
                collection && (
                  <NftBuyItemBox
                    collection={collection}
                    nft={nft}
                    onBought={refreshNft}
                  />
                )}

              {nft?.ownerAddress &&
                address != nft.ownerAddress &&
                nft &&
                collection && (
                  <NftBidItemBox collection={collection} nft={nft} />
                )}
            </div>
          </div>

          <div className="scrollbar-custom mt-14 overflow-x-auto rounded-lg">
            <div className="min-w-fit">
              <NftTabs
                items={(() => {
                  const conditionalTabs: Record<string, JSX.Element> = {};
                  if (
                    (bidInfo?.bids.length ?? 0) > 0 &&
                    collection &&
                    nft &&
                    nft.ownerAddress == address
                  )
                    conditionalTabs["Bids"] = (
                      <BidsTab
                        onAccepted={async () => {
                          await refreshNft();
                        }}
                        nft={nft}
                        collection={collection}
                        bidInfo={bidInfo!}
                      />
                    );

                  return {
                    ...conditionalTabs,
                    Traits: traits && <TraitsTab traits={traits} />,
                    Detail: nft && collection && (
                      <DetailTab nft={nft} collection={collection} />
                    ),
                  };
                })()}
              />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
